import { Layout } from 'components/common/layout/Layout';
import { GetStaticProps } from 'next';
import { container } from 'tsyringe';
import { gql, useQuery } from 'urql';
import { GenericFullPageLoading } from '../../../components/common/GenericFullPageLoading';
import { CustomSEO } from '../../../components/common/layout/HeadMeta';
import { LatestNews } from '../../../components/home/LatestNews';
import { FeaturedColleges } from '../../../components/partners-new/ind-partners/FeaturedColleges';
import { FeaturedPlayers } from '../../../components/partners-new/ind-partners/FeaturedPlayers';
import { FeaturedSchools } from '../../../components/partners-new/ind-partners/FeaturedSchools';
import { PartnersHeaderTabs } from '../../../components/partners-new/ind-partners/PartnersHeaderTabs';
import { PlayVsLogo } from '../../../components/svg/partners/PlayVsLogo';
import {
    OrgIhsenRecruitingDocument,
    OrgIhsenRecruitingQuery,
    OrgPlayVsRecruitingDocument,
    OrgPlayVsRecruitingQuery,
} from '../../../generated/spin-graphql';
import { ContentfulService } from '../../../server/services/contentful.service';
import { UrqlSSGForPage } from '../../../server/utils/serverUtils/ssg.utils';
import { PostSummary } from '../../../types/shared.types';

gql`
    query OrgPlayVsRecruiting {
        espin_college(limit: 12) {
            ...CollegeCard
            id
            name
            program_name
            subscribed
            address {
                city
                state
                country
            }
        }
        espin_org_player(
            where: { _and: [{ org: { definition: { _eq: "playvs" } } }, { featured: { _eq: true } }] }
            limit: 8
        ) {
            player_review
            rank
            featured
            gpa
            grad_class
            game_rank
            username
            game {
                name
                game_definition
            }
            school {
                display_name
                city
                state
            }
        }
        espin_org_school(
            where: { _and: [{ org: { definition: { _eq: "playvs" } } }, { featured: { _eq: true } }] }
            limit: 6
        ) {
            display_name
            city
            state
            rank
            bio
            featured
            records {
                wins
                losses
                game {
                    game_definition
                    name
                }
            }
        }
    }
`;

export default function IhsenRec({ news }: { news?: Array<PostSummary> }) {
    const title = 'IEN Recruiting';
    const description = 'Stay up to date on all things esports recruiting in the state of Indiana here.';
    const seoOverride: CustomSEO = {
        title: title,
        description: description,
    };

    const [result] = useQuery<OrgPlayVsRecruitingQuery>({ query: OrgPlayVsRecruitingDocument });
    const colleges = result?.data?.espin_college ?? [];
    const players = result?.data?.espin_org_player ?? [];
    const schools = result?.data?.espin_org_school ?? [];

    if (result.fetching) {
        return <GenericFullPageLoading />;
    }

    return (
        <Layout seoOverride={seoOverride}>
            <PartnersHeaderTabs
                tabOneTitle={'General'}
                tabOneRoute={'/partners/playvs'}
                tabTwoTitle={'Recruiting'}
                tabTwoRoute={'/partners/playvs/recruiting'}
                tabThreeTitle={'Rankings'}
                tabThreeRoute={'/partners/playvs/rankings'}
                icon={<PlayVsLogo className="playvs" />}
            />
            <LatestNews news={news} />
            <FeaturedPlayers players={players} />
            <FeaturedSchools schools={schools} />
            <FeaturedColleges colleges={colleges} regionName={'Across the nation'} />
        </Layout>
    );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
    const contentfulService = container.resolve(ContentfulService);
    const contentResponse = await contentfulService.getRecentPosts();
    const ssgResponse = await UrqlSSGForPage(OrgPlayVsRecruitingDocument);

    if (contentResponse) {
        return {
            props: {
                ...ssgResponse.props,
                news: contentResponse.postCollection.items,
            },
            revalidate: ssgResponse.revalidate,
        };
    }

    return ssgResponse;
};
